/**
 * VrWorldPanel — the in-headset panel: nine pages on one canvas quad (XR P2 "VR UI / UX", 2026-09-28).
 *
 *   Main · My Worlds · HUD        (kept from the original panel)
 *   Comfort   the locomotion comfort model (lib/xr_comfort) — this page only WRITES the settings through
 *             `setComfort`; the locomotion slice reads them and owns every behaviour
 *   Perf      `xrPerfSnapshot()` at ~2 Hz while the page is up
 *   Chat      the lines the chat module SHOWED (its ring), read-only, paged from the newest
 *   Inventory items and stacks, read-only; a weapon not in hand gets Equip (one `swapWeapon` call)
 *   Map       a north-up local map from the local map module's own layers, zoomable
 *   Dialogue  the versona you are talking to: the last line, its topics (asked through the SAME path as the DOM talk
 *             window), Goodbye; "talk to the nearest" when you are not in a conversation
 *
 * Laws: WORLD-ANCHORED (opened `PANEL.distanceM` ahead, a little below the eye, facing it, then it stays where it
 * was put — never head-locked); ONE MESH; the canvas is repainted only when its signature moves (page, the sampled
 * data's key, the hovered widgets, the status line); data is SAMPLED on a per-page cadence, never every frame;
 * every colour and face is the theme's. The widgets hit-tested are the ones the last paint drew (hit what you see).
 */

import { VrCanvasPanel } from './VrCanvasPanel.js';
import { col, colA, font, slab, icon, text, bar, button, levelColor, withAlpha } from './vr_draw.js';
import * as data from './vr_data.js';
import {
  PANEL, FONT, LAYOUT, PAGES, frameWidgets, hitWidget, listLayout, wrapText, comfortTiles, sectorComfortTiles, comfortTileRects, stepZone,
  comfortClick, perfTiles, statTileRects, MAP_RADII, MAP_RECT, MAP_SIDE, mapProject, rowRects, isPageId,
  type PageId, type Widget, type Rect, type ComfortTileId,
} from '../../lib/xr_ui_layout.js';
import { panelPose, type V3 } from '../../lib/xr_ui_input.js';
import { comfort, setComfort, resetComfort } from '../../lib/xr_comfort.js';
import { xrPerfSnapshot } from '../../lib/xr_perf.js';
import { inventoryRows, cssVarToken, type InvRow } from '../../lib/xr_ui_data.js';
import { recentChatLines } from '../AtlasChatModule.js';
import { CATEGORY_INFO } from '../../lib/map_structures.js';
import { myWaypoints, trackedWaypoint, WAYPOINT_COLOR } from '../../lib/waypoints.js';
import { themeColor } from '../../lib/ui_theme.js';
import { atlas } from '../../state.js';

/** The NpcInteract module's VR accessors (the SAME conversation as the DOM talk window). */
export interface NpcTalkLike {
  xrTalk(): { name: string; line: string; topics: Array<{ key: string; label: string }> } | null;
  xrAsk(key: string): void;
  xrTalkNearest(): boolean;
  xrGoodbye(): void;
}
/** The local map module's layer accessor. */
export interface LocalMapLike {
  xrLayers(radiusM: number): {
    buildings: Array<{ x: number; z: number }>;
    mapBuildings: Array<{ x: number; z: number; w: number; d: number; yaw: number; category: string; name: string }>;
    ports: Array<{ x: number; z: number; name: string }>;
    deeds: Array<{ x: number; z: number; name: string }>;
    cities: Array<{ x: number; z: number; r: number; name: string }>;
    players: Array<{ x: number; z: number }>;
    versonas: Array<{ x: number; z: number }>;
    creatures: Array<{ x: number; z: number }>;
    siteNames: Map<number, string>;
    sites: Array<{ id: number; centerX: number; centerZ: number }>;
  } | null;
}

export interface WorldPanelHooks {
  npc(): NpcTalkLike | null;
  localMap(): LocalMapLike | null;
  enterWorld(worldId: bigint): void;
  exitVr(): void;
  /** Re-open the panel in front of the head (Main → "Bring the panel here"). */
  recenter(): void;
  /** The head's WORLD heading, radians (three's yaw: 0 = looking down −Z) — the map's arrow. */
  headHeading(): number;
  /** The hand the wrist menu lives on (the Main page's hint names it). */
  wristHand(): 'left' | 'right';
  /** Whether the session has an X button (the hint says X, or the orb). */
  hasXButton(): boolean;
}

/** How often each page re-reads its data while it is up, seconds. */
const CADENCE: Record<PageId, number> = { main: 2, worlds: 2, hud: 1, comfort: 1, perf: 0.5, chat: 0.25, inventory: 1, map: 1, dialogue: 1 };
const INV_ROWS = 7;
const CHAT_LINE_H = 34;

export class VrWorldPanel {
  readonly surface = new VrCanvasPanel('vr-world-panel', PANEL.wM, PANEL.hM, PANEL.cw, PANEL.ch, 999);
  page: PageId = 'main';
  private widgets: Widget[] = frameWidgets();
  private readonly actions = new Map<string, (zone: 'left' | 'mid' | 'right') => void>();
  private status = '';
  private listPage: Partial<Record<PageId, number>> = {};
  private chatFromEnd = 0;
  private mapRadius = 1;
  private sinceSample = 999;
  private dataKey = '';
  private sample = 0;
  private hoverKey = '';
  private theme = '';
  // Sampled data (read by the painter; never re-read while painting).
  private perf = xrPerfSnapshot();
  private chat: ReadonlyArray<{ who: string; text: string; color: string }> = [];
  private inv: InvRow[] = [];
  private talk: ReturnType<NpcTalkLike['xrTalk']> = null;
  private map: ReturnType<LocalMapLike['xrLayers']> = null;
  private mapCenter = { x: 0, z: 0 };
  opens = 0;
  clicks = 0;
  lastAction = '';

  constructor(private readonly hooks: WorldPanelHooks) {}

  get open(): boolean { return this.surface.visible; }

  /** Open in front of the head (rig-local), facing it. */
  show(head: V3, headFwd: V3, page?: PageId): void {
    const { pos, rot } = panelPose(head, headFwd, PANEL.distanceM, PANEL.dropM);
    this.surface.setPose(pos, rot);
    this.surface.visible = true;
    if (page) this.setPage(page);
    this.sinceSample = 999;
    this.surface.invalidate();
    this.opens++;
  }

  hide(): void { this.surface.visible = false; }

  setPage(p: PageId): void {
    if (!isPageId(p)) return;
    if (p === 'chat' && this.page !== 'chat') this.chatFromEnd = 0;
    this.page = p;
    this.sinceSample = 999;
    this.surface.invalidate();
  }

  setStatus(t: string): void { this.status = t; }
  invalidate(): void { this.surface.invalidate(); this.sinceSample = 999; }

  /** A rig-local ray against the panel: `panel:<widget>` (or `panel:` over the body — it still takes the ray). */
  hit(o: V3, d: V3): { target: string; t: number; point: V3 } | null {
    const h = this.surface.hit(o, d);
    if (!h) return null;
    const w = hitWidget(this.widgets, h.px, h.py);
    if (!w) return { target: 'panel:', t: h.t, point: h.point };
    const id = w.id.startsWith('tile:') ? `${w.id}:${stepZone(w.rect, h.px)}` : w.id;
    return { target: `panel:${id}`, t: h.t, point: h.point };
  }

  /** A completed click on a widget id (without the `panel:` prefix). */
  click(id: string): void {
    if (!id) return;
    this.clicks++;
    this.lastAction = id;
    if (id.startsWith('page:')) { this.setPage(id.slice(5) as PageId); return; }
    if (id === 'close:panel') { this.hide(); return; }
    let base = id;
    let zone: 'left' | 'mid' | 'right' = 'mid';
    if (id.startsWith('tile:')) {
      const parts = id.split(':');
      base = `tile:${parts[1]}`;
      if (parts[2] === 'left' || parts[2] === 'right') zone = parts[2];
    }
    this.actions.get(base)?.(zone);
    this.sinceSample = 999;
    this.surface.invalidate();
  }

  /** Per frame while presenting: sample on the page's cadence, repaint when the signature moved. */
  tick(dt: number, hovers: Set<string>): void {
    if (!this.surface.visible) return;
    this.sinceSample += dt;
    if (this.sinceSample >= CADENCE[this.page]) {
      this.sinceSample = 0;
      const key = this.sampleNow();
      if (key !== this.dataKey) { this.dataKey = key; this.sample++; }
    }
    const hov: string[] = [];
    for (const h of hovers) if (h.startsWith('panel:') && h.length > 6) hov.push(h.slice(6));
    hov.sort();
    this.hoverKey = hov.join(',');
    const theme = document.documentElement.dataset.axTheme ?? '';
    if (theme !== this.theme) { this.theme = theme; this.surface.invalidate(); }
    const hoverSet = new Set(hov);
    this.surface.paint(`${this.page}|${this.sample}|${this.hoverKey}|${this.status}|${theme}`, (g) => this.draw(g, hoverSet));
  }

  // ─── Sampling ─────────────────────────────────────────────────────────────────

  /** Read the current page's data; returns a key that changes only when what is drawn would change. */
  private sampleNow(): string {
    switch (this.page) {
      case 'perf': {
        this.perf = xrPerfSnapshot();
        const s = this.perf;
        return `${s.frames}|${s.p50Ms.toFixed(1)}|${s.p95Ms.toFixed(1)}|${(s.missShare * 1000) | 0}|${s.draws}|${Math.round(s.triangles / 1000)}|${s.level}|${s.foveation.toFixed(2)}|${s.framebufferScale.toFixed(2)}|${s.targetHz}|${s.headset}`;
      }
      case 'chat': {
        const r = recentChatLines(40);
        this.chat = r.lines;
        return `v${r.version}|${this.chatFromEnd}`;
      }
      case 'inventory': {
        const { items, stacks } = data.inventory();
        const eq = data.equipped();
        this.inv = inventoryRows(items, stacks, eq.spec.key, eq.quality);
        return this.inv.map((r) => `${r.key}:${r.sub}`).join('|') + `|${this.listPage.inventory ?? 0}`;
      }
      case 'dialogue': {
        const t = this.hooks.npc()?.xrTalk() ?? null;
        this.talk = t;
        return t ? `${t.name}|${t.line}|${t.topics.map((x) => x.key).join(',')}|${this.listPage.dialogue ?? 0}` : 'none';
      }
      case 'map': {
        const r = MAP_RADII[this.mapRadius];
        this.mapCenter = { x: atlas.localX, z: atlas.localZ };
        this.map = this.hooks.localMap()?.xrLayers(r * 1.45) ?? null;
        const m = this.map;
        const wps = myWaypoints(atlas.conn).length;
        const head = Math.round((this.hooks.headHeading() * 180) / Math.PI / 5);
        return `${Math.round(this.mapCenter.x / 2)}|${Math.round(this.mapCenter.z / 2)}|${this.mapRadius}|${head}|${m ? `${m.players.length}|${m.versonas.length}|${m.creatures.length}|${m.mapBuildings.length}|${m.buildings.length}` : 'none'}|${wps}`;
      }
      case 'hud': {
        const w = data.wallet(), p = data.pulse();
        return `${w.sov}|${w.inf}|${p ? `${p.social}|${p.fun}|${p.creativity}|${p.rest}` : '-'}|${data.timeOfDay()}|${atlas.remotes.size}`;
      }
      case 'worlds': return data.myWorlds().map((w) => `${w.worldId}:${w.name}:${w.here}`).join('|') + `|${this.listPage.worlds ?? 0}`;
      case 'comfort': return JSON.stringify(comfort()) + (atlas.spaceFlight ? '|sector' : ''); // space campaign L: the sector's tiles
      default: return `${atlas.currentWorldId}|${atlas.personalWorldId}|${atlas.commonsWorldId}|${data.worldName()}|${this.hooks.wristHand()}|${this.hooks.hasXButton()}`;
    }
  }

  // ─── Painting ─────────────────────────────────────────────────────────────────

  private reg(id: string, rect: Rect, act?: (zone: 'left' | 'mid' | 'right') => void): void {
    this.widgets.push({ id, rect });
    if (act) this.actions.set(id, act);
  }

  private draw(g: CanvasRenderingContext2D, hov: Set<string>): void {
    this.widgets = frameWidgets();
    this.actions.clear();
    const W = PANEL.cw, H = PANEL.ch;
    // The slab.
    slab(g, { x: 2, y: 2, w: W - 4, h: H - 4 }, 22, colA('--ax-bg', 0.95, '#120d0a'), col('--ax-line2', '#6b5a48'), 3);
    // Header: the wordmark, the world, the close button.
    text(g, 'COSMOS', 26, 46, col('--ax-accent', '#d9a441'), font(FONT.title, 'display', 700));
    text(g, data.worldName() || '—', 200, 44, col('--ax-soft', '#cdbfa6'), font(22, 'body', 500), 620);
    const pageLabel = PAGES.find((p) => p.id === this.page)?.label ?? '';
    text(g, pageLabel, 930, 44, col('--ax-muted', '#8c7a66'), font(22, 'body', 600), 150, 'right');
    const cr = LAYOUT.close;
    const closeHov = hov.has('close:panel');
    slab(g, { x: cr.x + 4, y: cr.y + 4, w: cr.w - 8, h: cr.h - 8 }, 10, closeHov ? col('--ax-sel', '#3b2c1f') : null, closeHov ? col('--ax-hi', '#ffffff') : col('--ax-line', '#4b3e31'), 2);
    icon(g, 'close', cr.x + (cr.w - 30) / 2, cr.y + (cr.h - 30) / 2, 30, closeHov ? col('--ax-hi', '#ffffff') : col('--ax-soft', '#cdbfa6'));
    g.fillStyle = col('--ax-line', '#4b3e31');
    g.fillRect(12, LAYOUT.header.h, W - 24, 2);
    // The page rail.
    PAGES.forEach((p, i) => {
      const r: Rect = { x: LAYOUT.nav.x, y: LAYOUT.nav.y + i * LAYOUT.nav.rowH, w: LAYOUT.nav.w, h: LAYOUT.nav.rowH - 6 };
      const active = p.id === this.page;
      const h = hov.has(`page:${p.id}`);
      if (active) slab(g, r, 10, colA('--ax-accent', 0.2, '#d9a441'), h ? col('--ax-hi', '#ffffff') : null, 2);
      else if (h) slab(g, r, 10, col('--ax-sel', '#3b2c1f'), col('--ax-hi', '#ffffff'), 2);
      if (active) { g.fillStyle = col('--ax-accent', '#d9a441'); g.fillRect(r.x, r.y + 8, 5, r.h - 16); }
      icon(g, p.icon, r.x + 16, r.y + (r.h - 28) / 2, 28, active ? col('--ax-accent', '#d9a441') : col('--ax-soft', '#cdbfa6'));
      text(g, p.label, r.x + 58, r.y + r.h / 2 + 9, active || h ? col('--ax-hi', '#ffffff') : col('--ax-text', '#efe6d2'), font(FONT.nav, 'body', active ? 700 : 500), r.w - 66);
    });
    g.fillStyle = col('--ax-line', '#4b3e31');
    g.fillRect(LAYOUT.content.x - 8, LAYOUT.content.y, 2, LAYOUT.content.h);
    // The page.
    switch (this.page) {
      case 'main': this.drawMain(g, hov); break;
      case 'worlds': this.drawWorlds(g, hov); break;
      case 'hud': this.drawHud(g); break;
      case 'comfort': this.drawComfort(g, hov); break;
      case 'perf': this.drawPerf(g); break;
      case 'chat': this.drawChat(g, hov); break;
      case 'inventory': this.drawInventory(g, hov); break;
      case 'map': this.drawMap(g, hov); break;
      case 'dialogue': this.drawDialogue(g, hov); break;
    }
    // The footer: the latest status line (reducer feedback stays visible in-headset).
    g.fillStyle = col('--ax-line', '#4b3e31');
    g.fillRect(12, LAYOUT.footer.y, W - 24, 2);
    text(g, this.status || 'Point and pull the trigger — or pinch — to choose.', 24, LAYOUT.footer.y + 33, col('--ax-muted', '#8c7a66'), font(FONT.small, 'body', 500), W - 48);
  }

  private rowButton(g: CanvasRenderingContext2D, r: Rect, ic: string, label: string, hover: boolean, sub?: string, accent?: string): void {
    slab(g, r, 10, hover ? col('--ax-sel', '#3b2c1f') : col('--ax-bg2', '#1c1510'), hover ? col('--ax-hi', '#ffffff') : col('--ax-line', '#4b3e31'), 2);
    icon(g, ic, r.x + 18, r.y + (r.h - 30) / 2, 30, accent ?? (hover ? col('--ax-hi', '#ffffff') : col('--ax-accent', '#d9a441')));
    if (sub) {
      text(g, label, r.x + 64, r.y + r.h / 2 - 3, hover ? col('--ax-hi', '#ffffff') : col('--ax-text', '#efe6d2'), font(FONT.body, 'body', 600), r.w - 80);
      text(g, sub, r.x + 64, r.y + r.h / 2 + 22, col('--ax-muted', '#8c7a66'), font(FONT.small, 'body', 400), r.w - 80);
    } else {
      text(g, label, r.x + 64, r.y + r.h / 2 + 9, hover ? col('--ax-hi', '#ffffff') : col('--ax-text', '#efe6d2'), font(FONT.body, 'body', 600), r.w - 80);
    }
  }

  private pager(g: CanvasRenderingContext2D, hov: Set<string>, pageId: PageId, L: { page: number; pages: number; prev: Rect | null; next: Rect | null }): void {
    if (!L.prev || !L.next) return;
    const set = (p: number): void => { this.listPage[pageId] = p; };
    this.reg('list:prev', L.prev, () => set(Math.max(0, L.page - 1)));
    this.reg('list:next', L.next, () => set(Math.min(L.pages - 1, L.page + 1)));
    button(g, L.prev, '‹ Prev', hov.has('list:prev'), false, 22, L.page === 0);
    button(g, L.next, 'Next ›', hov.has('list:next'), false, 22, L.page >= L.pages - 1);
    text(g, `${L.page + 1} / ${L.pages}`, LAYOUT.content.x, L.prev.y + 36, col('--ax-muted', '#8c7a66'), font(FONT.small, 'mono', 500));
  }

  private drawMain(g: CanvasRenderingContext2D, hov: Set<string>): void {
    const rows: Array<{ id: string; icon: string; label: string; act: () => void }> = [];
    if (atlas.personalWorldId !== null && atlas.currentWorldId !== atlas.personalWorldId) {
      const w = atlas.personalWorldId;
      rows.push({ id: 'main:home', icon: 'home', label: 'Go home', act: () => this.hooks.enterWorld(w) });
    }
    if (atlas.commonsWorldId !== null && atlas.currentWorldId !== atlas.commonsWorldId) {
      const w = atlas.commonsWorldId;
      rows.push({ id: 'main:commons', icon: 'globe', label: 'Go to the Commons', act: () => this.hooks.enterWorld(w) });
    }
    rows.push({ id: 'main:recenter', icon: 'pin', label: 'Bring this panel in front of me', act: () => this.hooks.recenter() });
    rows.push({ id: 'main:exit', icon: 'exit', label: 'Exit VR', act: () => this.hooks.exitVr() });
    const rects = rowRects(rows.length, LAYOUT.content.y + 8, 64);
    rows.forEach((r, i) => {
      this.reg(r.id, rects[i], r.act);
      this.rowButton(g, rects[i], r.icon, r.label, hov.has(r.id), undefined, r.id === 'main:exit' ? col('--ax-bad', '#e0604a') : undefined);
    });
    const hand = this.hooks.wristHand();
    const help = [
      `Raise your ${hand} wrist and look at it for the quick menu — map, items, chat, talk, comfort.`,
      this.hooks.hasXButton() ? 'The X button opens and closes this panel.' : 'The glowing orb low in your view opens this panel.',
      'With tracked hands, pinch to choose; touch the wrist menu with a fingertip.',
    ];
    let y = (rects[rects.length - 1]?.y ?? LAYOUT.content.y) + 64 + 44;
    g.font = font(FONT.small, 'body', 400);
    for (const h of help) {
      for (const line of wrapText(h, LAYOUT.content.w - 8, (s) => g.measureText(s).width, 2)) {
        text(g, line, LAYOUT.content.x + 4, y, col('--ax-soft', '#cdbfa6'), font(FONT.small, 'body', 400));
        y += 28;
      }
      y += 6;
    }
  }

  private drawWorlds(g: CanvasRenderingContext2D, hov: Set<string>): void {
    const worlds = data.myWorlds();
    if (worlds.length === 0) {
      text(g, 'No owned worlds replicated yet.', LAYOUT.content.x + 4, LAYOUT.content.y + 44, col('--ax-muted', '#8c7a66'), font(FONT.body));
      text(g, 'Creating and renaming worlds stays on the desktop (it needs typing).', LAYOUT.content.x + 4, LAYOUT.content.y + 84, col('--ax-muted', '#8c7a66'), font(FONT.small), LAYOUT.content.w - 8);
      return;
    }
    const L = listLayout(worlds.length, this.listPage.worlds ?? 0, 7, LAYOUT.content.y + 8, 64);
    this.listPage.worlds = L.page;
    L.rows.forEach((r, i) => {
      const w = worlds[L.first + i];
      const id = `world:${w.worldId}`;
      if (!w.here) this.reg(id, r, () => this.hooks.enterWorld(w.worldId));
      this.rowButton(g, r, w.here ? 'pin' : 'arrow', w.name, !w.here && hov.has(id), w.here ? 'You are here' : 'Enter');
    });
    this.pager(g, hov, 'worlds', L);
  }

  private drawHud(g: CanvasRenderingContext2D): void {
    const C = LAYOUT.content;
    let y = C.y + 16;
    const w = data.wallet();
    icon(g, 'diamond', C.x + 4, y, 30, col('--ax-warn', '#e6b95a'));
    text(g, `${w.sov} SOV`, C.x + 44, y + 26, col('--ax-warn', '#e6b95a'), font(FONT.big, 'mono', 600));
    icon(g, 'star', C.x + 360, y, 30, col('--ax-good', '#7fc97f'));
    text(g, `${w.inf} INF`, C.x + 400, y + 26, col('--ax-good', '#7fc97f'), font(FONT.big, 'mono', 600));
    y += 72;
    const p = data.pulse();
    if (p) {
      for (const [label, v] of [['Social', p.social], ['Fun', p.fun], ['Creativity', p.creativity], ['Rest', p.rest]] as Array<[string, number]>) {
        text(g, label, C.x + 4, y + 22, col('--ax-text', '#efe6d2'), font(FONT.body));
        bar(g, { x: C.x + 190, y: y + 2, w: 460, h: 26 }, v / 100, col('--ax-info', '#6db3f2'), colA('--ax-text', 0.12, '#efe6d2'));
        text(g, `${Math.round(v)}`, C.x + 670, y + 24, col('--ax-soft', '#cdbfa6'), font(FONT.body, 'mono', 500));
        y += 48;
      }
    } else {
      text(g, 'Social Pulse not replicated yet.', C.x + 4, y + 22, col('--ax-muted', '#8c7a66'), font(FONT.body));
      y += 48;
    }
    y += 24;
    icon(g, 'clock', C.x + 4, y, 28, col('--ax-accent', '#d9a441'));
    text(g, `${data.timeOfDay()} in-world`, C.x + 44, y + 23, col('--ax-text', '#efe6d2'), font(FONT.body));
    icon(g, 'people', C.x + 360, y, 28, col('--ax-accent', '#d9a441'));
    const near = atlas.remotes.size;
    text(g, `${near} other traveler${near === 1 ? '' : 's'} near`, C.x + 400, y + 23, col('--ax-text', '#efe6d2'), font(FONT.body), 340);
  }

  private drawComfort(g: CanvasRenderingContext2D, hov: Set<string>): void {
    const c = comfort();
    // Space campaign L (2026-10-01): in the sector the sticks fly a ship — the page shows the SECTOR's comfort tiles.
    const tiles = atlas.spaceFlight ? sectorComfortTiles(c) : comfortTiles(c);
    const rects = comfortTileRects(tiles.length);
    tiles.forEach((t, i) => {
      const r = rects[i];
      const id = `tile:${t.id}`;
      const hz = [...hov].find((h) => h.startsWith(`${id}:`));
      const hover = !!hz;
      this.reg(id, r, (zone) => {
        const patch = comfortClick(comfort(), t.id as ComfortTileId, zone);
        if (patch === 'reset') resetComfort();
        else if (patch) setComfort(patch);
      });
      g.globalAlpha = t.dim ? 0.5 : 1;
      const action = t.kind === 'action';
      slab(g, r, 10, hover && t.kind !== 'step' ? col('--ax-sel', '#3b2c1f') : col('--ax-bg2', '#1c1510'), hover ? col('--ax-hi', '#ffffff') : col('--ax-line', '#4b3e31'), 2);
      const pad = t.kind === 'step' ? 70 : 18;
      text(g, t.label, r.x + pad, r.y + 32, col('--ax-muted', '#8c7a66'), font(FONT.small, 'body', 600), r.w - pad * 2);
      text(g, t.value, r.x + pad, r.y + 66, action ? col('--ax-bad', '#e0604a') : col('--ax-hi', '#ffffff'), font(FONT.body, 'body', 700), r.w - pad * 2);
      if (t.kind === 'step') {
        for (const side of ['left', 'right'] as const) {
          const sr: Rect = { x: side === 'left' ? r.x + 6 : r.x + r.w - 58, y: r.y + (r.h - 52) / 2, w: 52, h: 52 };
          const on = hz === `${id}:${side}`;
          slab(g, sr, 10, on ? col('--ax-sel', '#3b2c1f') : col('--ax-bg3', '#2a211a'), on ? col('--ax-hi', '#ffffff') : col('--ax-line2', '#6b5a48'), 2);
          text(g, side === 'left' ? '−' : '+', sr.x + sr.w / 2, sr.y + 38, col('--ax-text', '#efe6d2'), font(34, 'body', 700), undefined, 'center');
        }
      } else if (t.kind === 'cycle') {
        text(g, '›', r.x + r.w - 24, r.y + r.h / 2 + 12, col('--ax-soft', '#cdbfa6'), font(34, 'body', 700), undefined, 'center');
      }
      g.globalAlpha = 1;
    });
  }

  private drawPerf(g: CanvasRenderingContext2D): void {
    const tiles = perfTiles(this.perf);
    const rects = statTileRects(tiles.length);
    tiles.forEach((t, i) => {
      const r = rects[i];
      slab(g, r, 10, col('--ax-bg2', '#1c1510'), col('--ax-line', '#4b3e31'), 2);
      text(g, t.label, r.x + 14, r.y + 28, col('--ax-muted', '#8c7a66'), font(FONT.small, 'body', 600), r.w - 28);
      text(g, t.value, r.x + 14, r.y + 66, levelColor(t.level), font(FONT.big, 'mono', 600), r.w - 28);
      text(g, t.sub, r.x + 14, r.y + 94, col('--ax-soft', '#cdbfa6'), font(FONT.small, 'body', 400), r.w - 28);
      if (t.frac > 0) bar(g, { x: r.x + 14, y: r.y + r.h - 20, w: r.w - 28, h: 8 }, t.frac, levelColor(t.level), colA('--ax-text', 0.12, '#efe6d2'));
    });
    if (this.perf.frames === 0) text(g, 'Waiting for XR frames…', LAYOUT.content.x, LAYOUT.content.y + LAYOUT.content.h - 12, col('--ax-muted', '#8c7a66'), font(FONT.small));
  }

  private drawChat(g: CanvasRenderingContext2D, hov: Set<string>): void {
    const C = LAYOUT.content;
    const maxW = C.w - 16;
    g.font = font(FONT.body);
    const measure = (s: string): number => g.measureText(s).width;
    const lines: Array<{ s: string; color: string; who: boolean }> = [];
    for (const l of this.chat) {
      const color = this.chatColor(l.color);
      const head = l.who ? `${l.who}: ` : '';
      const wrapped = wrapText(`${head}${l.text}`, maxW, measure, 6);
      wrapped.forEach((s, i) => lines.push({ s, color, who: i === 0 && !!l.who }));
    }
    const per = Math.floor((C.h - 70) / CHAT_LINE_H);
    const maxBack = Math.max(0, lines.length - per);
    this.chatFromEnd = Math.max(0, Math.min(maxBack, this.chatFromEnd));
    const end = lines.length - this.chatFromEnd;
    const start = Math.max(0, end - per);
    if (lines.length === 0) text(g, 'No chat yet — nearby talk and system lines appear here.', C.x + 4, C.y + 40, col('--ax-muted', '#8c7a66'), font(FONT.body), maxW);
    let y = C.y + 32;
    for (let i = start; i < end; i++) {
      const l = lines[i];
      text(g, l.s, C.x + 6, y, l.color, font(FONT.body, 'body', l.who ? 600 : 400), maxW);
      y += CHAT_LINE_H;
    }
    const pagerY = C.y + C.h - 60;
    const older: Rect = { x: C.x + C.w - 340, y: pagerY, w: 160, h: 56 };
    const newer: Rect = { x: C.x + C.w - 170, y: pagerY, w: 160, h: 56 };
    this.reg('chat:older', older, () => { this.chatFromEnd = Math.min(maxBack, this.chatFromEnd + per); });
    this.reg('chat:newer', newer, () => { this.chatFromEnd = Math.max(0, this.chatFromEnd - per); });
    button(g, older, '‹ Older', hov.has('chat:older'), false, 22, this.chatFromEnd >= maxBack);
    button(g, newer, 'Newer ›', hov.has('chat:newer'), false, 22, this.chatFromEnd === 0);
    text(g, 'Read-only here — type on the desktop.', C.x, pagerY + 36, col('--ax-muted', '#8c7a66'), font(FONT.small), 400);
  }

  private chatColor(c: string): string {
    const tok = cssVarToken(c);
    if (tok) return themeColor(tok as Parameters<typeof themeColor>[0], '#efe6d2') || col('--ax-text', '#efe6d2');
    if (/^#[0-9a-f]{3,8}$/i.test(c) || /^rgba?\(/i.test(c)) return c;
    return col('--ax-text', '#efe6d2');
  }

  private drawInventory(g: CanvasRenderingContext2D, hov: Set<string>): void {
    const C = LAYOUT.content;
    const eq = data.equipped();
    const hp = data.vitals();
    text(g, `In hand: ${eq.spec.name}`, C.x + 4, C.y + 32, col('--ax-hi', '#ffffff'), font(FONT.body, 'body', 700), 420);
    text(g, `HP ${hp.hp} / ${hp.maxHp}`, C.x + 440, C.y + 32, col('--ax-good', '#7fc97f'), font(FONT.body, 'mono', 600), 140);
    if (eq.spec.key) {
      const r: Rect = { x: C.x + C.w - 170, y: C.y, w: 160, h: 48 };
      this.reg('inv:hands', r, () => this.equip(0n, 'Bare hands'));
      button(g, r, 'Put away', hov.has('inv:hands'), false, 20);
    }
    if (this.inv.length === 0) {
      text(g, 'Nothing carried.', C.x + 4, C.y + 100, col('--ax-muted', '#8c7a66'), font(FONT.body));
      return;
    }
    const L = listLayout(this.inv.length, this.listPage.inventory ?? 0, INV_ROWS, C.y + 60, 64);
    this.listPage.inventory = L.page;
    L.rows.forEach((r, i) => {
      const row = this.inv[L.first + i];
      slab(g, r, 10, row.inHand ? colA('--ax-accent', 0.16, '#d9a441') : col('--ax-bg2', '#1c1510'), col('--ax-line', '#4b3e31'), 2);
      icon(g, row.icon, r.x + 14, r.y + (r.h - 30) / 2, 30, row.inHand ? col('--ax-accent', '#d9a441') : col('--ax-soft', '#cdbfa6'));
      const textW = row.equipItemId !== null ? r.w - 250 : r.w - 80;
      text(g, row.title, r.x + 58, r.y + 28, col('--ax-text', '#efe6d2'), font(FONT.body, 'body', 600), textW);
      text(g, row.sub, r.x + 58, r.y + 54, col('--ax-muted', '#8c7a66'), font(FONT.small), textW);
      if (row.equipItemId !== null) {
        const id = `inv:equip:${row.equipItemId}`;
        const br: Rect = { x: r.x + r.w - 160, y: r.y + 6, w: 150, h: r.h - 12 };
        const itemId = row.equipItemId;
        this.reg(id, br, () => this.equip(itemId, row.title));
        button(g, br, 'Equip', hov.has(id), true, 22);
      }
    });
    this.pager(g, hov, 'inventory', L);
  }

  /** THE one inventory write: `swapWeapon` (0 = bare hands) — the same single transaction the desktop ring makes. */
  private equip(itemId: bigint, label: string): void {
    const r = atlas.conn?.reducers as unknown as { swapWeapon?: (a: { itemId: bigint }) => Promise<void> } | undefined;
    if (typeof r?.swapWeapon !== 'function') { this.status = 'Equip is not available yet.'; return; }
    this.status = `Equipping ${label}…`;
    r.swapWeapon({ itemId }).then(
      () => { this.status = itemId === 0n ? 'Weapon put away.' : `${label} in hand.`; this.invalidate(); },
      (err: unknown) => { this.status = `Could not equip: ${String((err as Error)?.message ?? err).slice(0, 80)}`; this.invalidate(); },
    );
  }

  private drawMap(g: CanvasRenderingContext2D, hov: Set<string>): void {
    const R = MAP_RECT;
    const radius = MAP_RADII[this.mapRadius];
    slab(g, R, 12, col('--ax-bg2', '#1c1510'), col('--ax-line2', '#6b5a48'), 2);
    const proj = mapProject(this.mapCenter.x, this.mapCenter.z, radius, R);
    const s = Math.min(R.w, R.h) / 2 / radius;
    g.save();
    g.beginPath();
    g.roundRect(R.x + 2, R.y + 2, R.w - 4, R.h - 4, 10);
    g.clip();
    // Range rings.
    g.strokeStyle = colA('--ax-text', 0.1, '#efe6d2');
    g.lineWidth = 1.5;
    for (const f of [0.25, 0.5, 0.75, 1]) { g.beginPath(); g.arc(R.x + R.w / 2, R.y + R.h / 2, (R.w / 2) * f, 0, Math.PI * 2); g.stroke(); }
    const m = this.map;
    if (m) {
      for (const c of m.cities) {
        const p = proj(c.x, c.z);
        g.setLineDash([8, 6]);
        g.strokeStyle = colA('--ax-accent', 0.8, '#d9a441');
        g.beginPath(); g.arc(p.px, p.py, Math.max(6, c.r * s), 0, Math.PI * 2); g.stroke();
        g.setLineDash([]);
      }
      // Buildings: the baked outlines (colour = the building's category, a data identity), else a square.
      for (const b of m.mapBuildings) {
        const p = proj(b.x, b.z);
        const info = (CATEGORY_INFO as Record<string, { colour: string }>)[b.category];
        g.save();
        g.translate(p.px, p.py);
        g.rotate(-b.yaw); // three's rotation.y turns local +x to (cos, −sin) on a north-up map: canvas angle −yaw
        g.fillStyle = withAlpha(info?.colour ?? col('--ax-soft', '#cdbfa6'), 0.75);
        const w = Math.max(2, b.w * s), d = Math.max(2, b.d * s);
        g.fillRect(-w / 2, -d / 2, w, d);
        g.restore();
      }
      if (m.mapBuildings.length === 0) {
        g.fillStyle = colA('--ax-soft', 0.6, '#cdbfa6');
        for (const b of m.buildings) { const p = proj(b.x, b.z); g.fillRect(p.px - 3, p.py - 3, 6, 6); }
      }
      g.fillStyle = col('--ax-warn', '#e6b95a');
      for (const d of m.deeds) { const p = proj(d.x, d.z); g.fillRect(p.px - 4, p.py - 4, 8, 8); }
      for (const pt of m.ports) { const p = proj(pt.x, pt.z); icon(g, 'pin', p.px - 11, p.py - 20, 22, col('--ax-info', '#6db3f2')); }
      g.fillStyle = colA('--ax-text', 0.55, '#efe6d2');
      for (const v of m.versonas) { const p = proj(v.x, v.z); g.beginPath(); g.arc(p.px, p.py, 3, 0, Math.PI * 2); g.fill(); }
      g.fillStyle = col('--ax-bad', '#e0604a');
      for (const c of m.creatures) { const p = proj(c.x, c.z); g.beginPath(); g.arc(p.px, p.py, 4.5, 0, Math.PI * 2); g.fill(); }
      g.fillStyle = col('--ax-good', '#7fc97f');
      for (const pl of m.players) { const p = proj(pl.x, pl.z); g.beginPath(); g.arc(p.px, p.py, 6, 0, Math.PI * 2); g.fill(); }
    }
    // Waypoints (the followed one ringed).
    const tracked = trackedWaypoint(atlas.conn);
    for (const w of myWaypoints(atlas.conn)) {
      if (w.worldId !== atlas.currentWorldId) continue;
      const p = proj(w.posX, w.posZ);
      g.save();
      g.translate(p.px, p.py);
      g.rotate(Math.PI / 4);
      g.fillStyle = WAYPOINT_COLOR;
      g.fillRect(-7, -7, 14, 14);
      g.restore();
      if (tracked && tracked.waypointId === w.waypointId) { g.strokeStyle = WAYPOINT_COLOR; g.lineWidth = 2; g.beginPath(); g.arc(p.px, p.py, 14, 0, Math.PI * 2); g.stroke(); }
    }
    // Me: an arrow along the head's heading (north up: heading 0 looks toward −Z = up).
    const h = this.hooks.headHeading();
    const cx = R.x + R.w / 2, cy = R.y + R.h / 2;
    g.save();
    g.translate(cx, cy);
    g.rotate(-h);
    g.fillStyle = col('--ax-hi', '#ffffff');
    g.strokeStyle = col('--ax-bg', '#120d0a');
    g.lineWidth = 3;
    g.beginPath(); g.moveTo(0, -16); g.lineTo(11, 12); g.lineTo(0, 6); g.lineTo(-11, 12); g.closePath();
    g.stroke(); g.fill();
    g.restore();
    g.restore();
    text(g, 'N', R.x + R.w / 2, R.y + 30, col('--ax-accent', '#d9a441'), font(24, 'display', 700), undefined, 'center');
    // The side column: zoom, scale, where.
    const S = MAP_SIDE;
    const zin: Rect = { x: S.x, y: S.y, w: S.w, h: 64 };
    const zout: Rect = { x: S.x, y: S.y + 76, w: S.w, h: 64 };
    this.reg('map:in', zin, () => { this.mapRadius = Math.max(0, this.mapRadius - 1); });
    this.reg('map:out', zout, () => { this.mapRadius = Math.min(MAP_RADII.length - 1, this.mapRadius + 1); });
    button(g, zin, 'Zoom in', hov.has('map:in'), false, 22, this.mapRadius === 0);
    button(g, zout, 'Zoom out', hov.has('map:out'), false, 22, this.mapRadius === MAP_RADII.length - 1);
    let y = S.y + 180;
    text(g, `${radius >= 1000 ? `${radius / 1000} km` : `${radius} m`}`, S.x, y, col('--ax-hi', '#ffffff'), font(FONT.body, 'mono', 600), S.w);
    text(g, 'to the edge', S.x, y + 26, col('--ax-muted', '#8c7a66'), font(FONT.small), S.w);
    y += 70;
    const place = this.nearestPlace();
    if (place) {
      text(g, 'Near', S.x, y, col('--ax-muted', '#8c7a66'), font(FONT.small), S.w);
      g.font = font(FONT.small, 'body', 600);
      for (const line of wrapText(place, S.w, (x) => g.measureText(x).width, 3)) { y += 26; text(g, line, S.x, y, col('--ax-text', '#efe6d2'), font(FONT.small, 'body', 600), S.w); }
      y += 40;
    }
    const legend: Array<[string, string]> = [[col('--ax-good', '#7fc97f'), 'Travelers'], [colA('--ax-text', 0.55, '#efe6d2'), 'Versonas'], [col('--ax-bad', '#e0604a'), 'Creatures'], [WAYPOINT_COLOR, 'Waypoints']];
    for (const [c, l] of legend) {
      g.fillStyle = c; g.beginPath(); g.arc(S.x + 8, y - 7, 6, 0, Math.PI * 2); g.fill();
      text(g, l, S.x + 22, y, col('--ax-soft', '#cdbfa6'), font(FONT.small), S.w - 24);
      y += 30;
    }
    if (!m) text(g, 'Map data not ready.', R.x + 20, R.y + R.h - 20, col('--ax-muted', '#8c7a66'), font(FONT.small));
  }

  private nearestPlace(): string | null {
    const m = this.map;
    if (!m) return null;
    let best: string | null = null;
    let bd = 2500;
    for (const s of m.sites) {
      const d = Math.hypot(s.centerX - this.mapCenter.x, s.centerZ - this.mapCenter.z);
      if (d < bd) { bd = d; best = m.siteNames.get(s.id) ?? null; }
    }
    return best;
  }

  private drawDialogue(g: CanvasRenderingContext2D, hov: Set<string>): void {
    const C = LAYOUT.content;
    const t = this.talk;
    const npc = this.hooks.npc();
    if (!t) {
      text(g, 'You are not talking to anyone.', C.x + 4, C.y + 44, col('--ax-text', '#efe6d2'), font(FONT.body, 'body', 600));
      const r: Rect = { x: C.x, y: C.y + 80, w: Math.min(C.w, 560), h: 64 }; // 420 px cut the label (pictured)
      this.reg('talk:nearest', r, () => {
        const ok = npc?.xrTalkNearest() ?? false;
        this.status = ok ? 'You turn to talk.' : 'No one is close enough to talk to.';
      });
      this.rowButton(g, r, 'talk', 'Talk to the nearest versona', hov.has('talk:nearest'));
      text(g, 'Walk up to a townsperson first — within a few steps.', C.x + 4, C.y + 186, col('--ax-muted', '#8c7a66'), font(FONT.small), C.w - 8);
      return;
    }
    text(g, t.name || 'Versona', C.x + 4, C.y + 36, col('--ax-accent', '#d9a441'), font(FONT.title, 'display', 700), C.w - 200);
    const bye: Rect = { x: C.x + C.w - 170, y: C.y, w: 160, h: 52 };
    this.reg('talk:bye', bye, () => { npc?.xrGoodbye(); this.talk = null; });
    button(g, bye, 'Goodbye', hov.has('talk:bye'), false, 22);
    // The last line, in a quote slab.
    g.font = font(FONT.body);
    const lines = wrapText(t.line || '…', C.w - 36, (s) => g.measureText(s).width, 6);
    const qh = 20 + lines.length * 32;
    slab(g, { x: C.x, y: C.y + 64, w: C.w, h: qh }, 12, col('--ax-bg2', '#1c1510'), col('--ax-line', '#4b3e31'), 2);
    lines.forEach((l, i) => text(g, l, C.x + 18, C.y + 64 + 36 + i * 32, col('--ax-text', '#efe6d2'), font(FONT.body)));
    // Topics: a two-column grid, paged.
    const top = C.y + 64 + qh + 18;
    const cols = 2, chipH = 56, gap = 10;
    const rowsFit = Math.max(1, Math.floor((C.y + C.h - 64 - top) / (chipH + gap)));
    const per = rowsFit * cols;
    const pages = Math.max(1, Math.ceil(t.topics.length / per));
    const page = Math.max(0, Math.min(pages - 1, this.listPage.dialogue ?? 0));
    this.listPage.dialogue = page;
    const w = (C.w - gap) / cols;
    t.topics.slice(page * per, page * per + per).forEach((tp, i) => {
      const r: Rect = { x: C.x + (i % cols) * (w + gap), y: top + Math.floor(i / cols) * (chipH + gap), w, h: chipH };
      const id = `topic:${tp.key}`;
      this.reg(id, r, () => { npc?.xrAsk(tp.key); this.sinceSample = 0.7; });
      button(g, r, tp.label, hov.has(id), false, 22);
    });
    if (t.topics.length === 0) text(g, '(nothing more to ask)', C.x + 4, top + 30, col('--ax-muted', '#8c7a66'), font(FONT.small));
    if (pages > 1) this.pager(g, hov, 'dialogue', { page, pages, prev: { x: C.x + C.w - 340, y: C.y + C.h - 60, w: 160, h: 56 }, next: { x: C.x + C.w - 170, y: C.y + C.h - 60, w: 160, h: 56 } });
  }

  /** The probe's view: page, widgets (with rects), the counters. */
  snapshot(): Record<string, unknown> {
    return {
      open: this.open, page: this.page, opens: this.opens, clicks: this.clicks, lastAction: this.lastAction, status: this.status,
      paints: this.surface.paints, hoverKey: this.hoverKey,
      pos: this.surface.pos(), rot: this.surface.rot(),
      widgets: this.widgets.map((w) => ({ id: w.id, rect: w.rect })),
      mapRadius: MAP_RADII[this.mapRadius], chatLines: this.chat.length, invRows: this.inv.length,
      talk: this.talk ? { name: this.talk.name, topics: this.talk.topics.length, line: this.talk.line.slice(0, 80) } : null,
    };
  }

  dispose(): void { this.surface.dispose(); }
}
